import { ChangeDetectionStrategy, Component, Input, OnInit, signal, ViewEncapsulation, WritableSignal, inject } from '@angular/core';
import { NgxTimelineEvent, NgxTimelineEventGroup, NgxTimelineItemPosition } from '@frxjs/ngx-timeline';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { startLoader, stopLoader } from '@skychute/ui/loader';
import { Activity_Log_Action_Enum, Activity_Log_Bool_Exp } from '@skychute/schema';
import { ActivityLogService } from '../services/activity-log.service';
import { Router } from '@angular/router';

@Component({
    selector: 'skychute-activity-log',
    templateUrl: './activity-log.component.html',
    styleUrls: ['./activity-log.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ActivityLogComponent implements OnInit {
  private activityService = inject(ActivityLogService);
  private router = inject(Router);

  showLoading: WritableSignal<boolean> = signal(true);
  isUpdating: WritableSignal<boolean> = signal(false);
  loadingText: WritableSignal<string> = signal('');
  events: WritableSignal<NgxTimelineEvent[]> = signal([]);
  form: FormGroup<ActivityForm>;
  readonly groupEvent = NgxTimelineEventGroup.DAY_MONTH_YEAR;

  @Input() contactId: string = null;
  @Input() listingId: string = null;
  @Input() dealId: string = null;
  @Input() userId: string = null;
  @Input() teamId: string = null;

  @Input() set defaultEvents(events: NgxTimelineEvent[]) {
    const existingTitles = new Set(this.events().map(e => e.title));
    const newEvents = events.filter(e => !existingTitles.has(e.title));
    if (newEvents.length === 0) {
      return; // No new default events to add
    }
    this.events.set([...this.events(), ...newEvents]);
  }

  async ngOnInit(): Promise<void> {
    this.initForm();

    await this.fetchActivityLogs();
  }

  private async fetchActivityLogs(): Promise<void> {
    const where: Activity_Log_Bool_Exp = {};

    if (this.contactId) {
      where.contact_id = { _eq: this.contactId };
    }

    if (this.listingId) {
      where.listing_id = { _eq: this.listingId };
    }
    if (this.dealId) {
      where.deal_id = { _eq: this.dealId };
    }
    if (this.userId) {
      where.user_id = { _eq: this.userId };
    }
    if (this.teamId) {
      where.team_id = { _eq: this.teamId };
    }
    this.showLoader('Loading activity logs...');

    const activityLogs = await this.activityService.get(where);
    const events = [];
    for (const [index, log] of activityLogs.entries()) {
      events.push({
        title: log.title,
        timestamp: new Date(log.created_at),
        description: log.description,
        id: log.id,
        itemPosition: index % 2 === 0 ? NgxTimelineItemPosition.ON_LEFT : NgxTimelineItemPosition.ON_RIGHT,
        dealId: log.deal_id,
      });
    }

    // Add default events if no activity logs are found
    if (events.length > 0) {
      const existingTitles = new Set(this.events().map(e => e.title));
      const newEvents = events.filter(e => !existingTitles.has(e.title));
      if (newEvents.length === 0) {
        return; // No new default events to add
      }

      const mergedEvents = [...newEvents, ...this.events()].map((event, idx) => ({
        ...event,
        itemPosition: (idx) % 2 === 0 ? NgxTimelineItemPosition.ON_LEFT : NgxTimelineItemPosition.ON_RIGHT,
      }));
      this.events.set(mergedEvents);
    }

    this.hideLoader();
  }

  private initForm(): void {
    this.form = new FormGroup<ActivityForm>({
      note: new FormControl<string | null>(null, [Validators.required]),
    });
  }

  showLoader(text = ''): void {
    this.showLoading.set(true);
    startLoader(text);
  }

  hideLoader(): void {
    this.showLoading.set(stopLoader());
  }

  async onUpdate(): Promise<void> {
    this.isUpdating.set(true);
    await this.activityService.create({
      contact_id: this.contactId,
      deal_id: this.dealId,
      listing_id: this.listingId,
      user_id: this.userId,
      team_id: this.teamId,
      description: this.form.value.note ?? '',
      title: 'Note added',
      activity: Activity_Log_Action_Enum.Note,
    });

    this.form.reset();
    await this.fetchActivityLogs();
    this.isUpdating.set(false);
  }

  async onTimelineClick(event): Promise<void> {
    if (!event) {
      // nothing to do if no event is clicked
      return;
    }
    console.log('Clicked event:', event);
    if (event.eventInfo?.dealId) {
      // want to navigate to the deal page
      await this.router.navigate(['/pages/deals', event.eventInfo.dealId]);
    }
  }
}

interface ActivityForm {
  note: FormControl<string | null>;
}
